/**
 * 建單 wizard 共用型別與工具（20260611 反饋批次）
 *
 * - DraftStop：wizard 編輯中的停點草稿（poiId 與 zone 至少一個才算完整）
 * - FormOptions：bookingData.formOptions 的回傳型別（車款/POI/包車方案/加購）
 * - buildOrderPayload：wizard state → order.create / order.estimate 入參；
 *   未填齊回 null（estimate query 以此控制 enabled）
 */
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../../server/routers";
import type { Loc, Region } from "../../../../../shared/const";
import {
  airportRegion,
  zoneByCode,
  type AddonCode,
  type BookingAirportCode,
  type BookingMode,
  type CreatableServiceType,
  type ZoneCode,
} from "../../../../../shared/booking";

export type FormOptions = inferRouterOutputs<AppRouter>["bookingData"]["formOptions"];
export type VehicleModelOption = FormOptions["vehicleModels"][number];
export type PoiOption = FormOptions["pois"][number];
export type CharterPlanOption = FormOptions["charterPlans"][number];
export type AddonOption = FormOptions["addonServices"][number];

/** wizard 編輯中的停點：poiId 有值＝從清單選（zone 隨 POI 帶入）；否則自行輸入＋必選 zone */
export type DraftStop = {
  location: string;
  zone: ZoneCode | "";
  poiId: number | null;
};

export const emptyStop = (): DraftStop => ({ location: "", zone: "", poiId: null });

/** 單一停點是否填妥（POI 已選、或 自行輸入＋zone） */
export const stopComplete = (s: DraftStop): boolean =>
  s.poiId !== null || (s.location.trim() !== "" && s.zone !== "");

export type WizardState = {
  serviceType: CreatableServiceType | null;
  /** 接機/送機 */
  airportCode: BookingAirportCode | "";
  bookingMode: BookingMode;
  flightNumber: string;
  /** 接機目的地 / 送機上車點 / 點對點停點 */
  stops: DraftStop[];
  /** 包車 */
  charterRegion: Region;
  charterPickupLocation: string;
  charterDropoffLocation: string;
  charterDurationHours: number | null;
  /** 共通 */
  pickupTimeLocal: string;
  vehicleModelId: number | null;
  addonCodes: AddonCode[];
  passengerName: string;
  passengerPhoneCc: string;
  passengerPhone: string;
  passengerCount: number;
  luggageCount: number;
  /** D7：即時通訊四獨立欄（各自儲存）；與電話整組至少填一個 */
  passengerWhatsapp: string;
  passengerLine: string;
  passengerWechat: string;
  passengerKakao: string;
  specialRequests: string;
};

/**
 * 服務區域推導（前端鏡像 §2.2，供車款/POI 過濾）：未定 → null。
 * 權威實作：`shared/booking-pricing.ts` 的 `deriveBookingRegion`（server 經
 * `booking-pricing.service.ts` 使用）— 規則變更時兩處同步。
 * 已知草稿期差異：點對點取「第一個已填 zone 的停點」（server 為 zones[0]），
 * payload 填齊後兩者等價。
 */
export function deriveWizardRegion(s: WizardState): Region | null {
  switch (s.serviceType) {
    case "airport_pickup":
    case "airport_dropoff":
      return s.airportCode === "" ? null : airportRegion(s.airportCode);
    case "point_to_point": {
      const first = s.stops.find((st) => st.zone !== "");
      return first ? (zoneByCode(first.zone)?.region ?? null) : null;
    }
    case "charter":
      return s.charterRegion;
    default:
      return null;
  }
}

type StopPayload = { location: string; zone?: ZoneCode; poiId?: number };

const stopsPayload = (stops: DraftStop[]): StopPayload[] | null => {
  if (stops.length === 0 || !stops.every(stopComplete)) return null;
  return stops.map((s) => ({
    location: s.location.trim(),
    ...(s.zone !== "" ? { zone: s.zone } : {}),
    ...(s.poiId !== null ? { poiId: s.poiId } : {}),
  }));
};

export type OrderPayload =
  | {
      serviceType: "airport_pickup";
      airportCode: BookingAirportCode;
      bookingMode: BookingMode;
      flightNumber?: string;
      stops: StopPayload[];
    }
  | { serviceType: "airport_dropoff"; airportCode: BookingAirportCode; flightNumber: string; stops: StopPayload[] }
  | { serviceType: "point_to_point"; stops: StopPayload[] }
  | { serviceType: "charter"; pickupLocation: string; dropoffLocation: string; charterPlanId: number };

export type FullOrderPayload = OrderPayload & {
  vehicleModelId: number;
  pickupTime: number;
  passengerName: string;
  passengerPhone?: string;
  passengerCount: number;
  luggageCount: number;
  addonCodes: AddonCode[];
  passengerWhatsapp?: string;
  passengerLine?: string;
  passengerWechat?: string;
  passengerKakao?: string;
  specialRequests?: string;
};

/**
 * wizard state → order.create / order.estimate 入參。
 * 任一必填缺漏（含包車方案無對應 region×車款×時數）→ null。
 * 條件必填規則鏡像自 `server/services/order.service.ts` 的 `createOrderInput`
 * superRefine（by_flight ⇒ flightNumber、stop 須 poiId 或 zone）— 改規則時兩處同步；
 * 欄位形狀漂移由 tRPC 端到端型別在 pnpm check 把關。
 */
export function buildOrderPayload(s: WizardState, charterPlans: CharterPlanOption[]): FullOrderPayload | null {
  if (s.serviceType === null || s.vehicleModelId === null) return null;
  const pickupTime = s.pickupTimeLocal === "" ? NaN : new Date(s.pickupTimeLocal).getTime();
  if (!Number.isFinite(pickupTime) || pickupTime <= Date.now()) return null;
  if (s.passengerName.trim() === "" || s.passengerCount < 1) return null;

  // D7 A6：電話含國碼；A9：電話＋四通訊整組至少填一個
  const phoneNum = s.passengerPhone.trim();
  const phoneFull = phoneNum !== "" ? `${s.passengerPhoneCc} ${phoneNum}`.trim() : "";
  const whatsapp = s.passengerWhatsapp.trim();
  const line = s.passengerLine.trim();
  const wechat = s.passengerWechat.trim();
  const kakao = s.passengerKakao.trim();
  if (phoneFull === "" && whatsapp === "" && line === "" && wechat === "" && kakao === "") return null;

  const base = {
    vehicleModelId: s.vehicleModelId,
    pickupTime,
    passengerName: s.passengerName.trim(),
    ...(phoneFull !== "" ? { passengerPhone: phoneFull } : {}),
    ...(whatsapp !== "" ? { passengerWhatsapp: whatsapp } : {}),
    ...(line !== "" ? { passengerLine: line } : {}),
    ...(wechat !== "" ? { passengerWechat: wechat } : {}),
    ...(kakao !== "" ? { passengerKakao: kakao } : {}),
    passengerCount: s.passengerCount,
    luggageCount: s.luggageCount,
    addonCodes: s.addonCodes,
    ...(s.specialRequests.trim() !== "" ? { specialRequests: s.specialRequests.trim() } : {}),
  };

  switch (s.serviceType) {
    case "airport_pickup": {
      if (s.airportCode === "") return null;
      if (s.bookingMode === "by_flight" && s.flightNumber.trim() === "") return null;
      const stops = stopsPayload(s.stops);
      if (!stops) return null;
      return {
        ...base,
        serviceType: "airport_pickup",
        airportCode: s.airportCode,
        bookingMode: s.bookingMode,
        ...(s.flightNumber.trim() !== "" ? { flightNumber: s.flightNumber.trim() } : {}),
        stops,
      };
    }
    case "airport_dropoff": {
      if (s.airportCode === "" || s.flightNumber.trim() === "") return null;
      const stops = stopsPayload(s.stops);
      if (!stops) return null;
      return {
        ...base,
        serviceType: "airport_dropoff",
        airportCode: s.airportCode,
        flightNumber: s.flightNumber.trim(),
        stops,
      };
    }
    case "point_to_point": {
      const stops = stopsPayload(s.stops);
      if (!stops || stops.length < 2) return null;
      return { ...base, serviceType: "point_to_point", stops };
    }
    case "charter": {
      if (
        s.charterPickupLocation.trim() === "" ||
        s.charterDropoffLocation.trim() === "" ||
        s.charterDurationHours === null
      )
        return null;
      const plan = charterPlans.find(
        (p) =>
          p.region === s.charterRegion &&
          p.vehicleModelId === s.vehicleModelId &&
          p.durationHours === s.charterDurationHours,
      );
      if (!plan) return null;
      return {
        ...base,
        serviceType: "charter",
        pickupLocation: s.charterPickupLocation.trim(),
        dropoffLocation: s.charterDropoffLocation.trim(),
        charterPlanId: plan.id,
      };
    }
  }
}

/** 共用 i18n 標籤 */
export const REGION_LABEL: Record<Region, Loc> = {
  taiwan: { "zh-TW": "台灣", "en-US": "Taiwan", "zh-CN": "台湾" },
  hongkong: { "zh-TW": "香港", "en-US": "Hong Kong", "zh-CN": "香港" },
};

export const zoneLabel = (code: ZoneCode | ""): Loc =>
  (code !== "" ? zoneByCode(code)?.label : undefined) ?? { "zh-TW": "—", "en-US": "—", "zh-CN": "—" };

/** 顯示金額（display currency 已含換算）；HKD 明細顯示用 */
export const fmtMoney = (currency: string, amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const v = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(v)) return "—";
  return `${currency} ${v.toLocaleString("zh-Hant", { maximumFractionDigits: 2 })}`;
};
